/**
 * 昨夜の睡眠のまとめ（「今日」タブの一番上）
 *
 * 結果より今後を先に見せるため、AI の「今日やること」を気づきより上に置く。
 * 「次の就寝の目安」は今夜の計画と食い違うことがあるので出さない（就寝はタイムラインの計画に任せる）。
 */

import { ArrowRight, ChevronRight, Moon } from 'lucide-react'

import type { SleepDayDetail } from '@/api/types/sleep'

import { formatJstTime, formatMinutes, QUALIFIER_LABEL } from '../format'
import { ToneIcon } from './ai-report-card'

const MAX_FINDINGS = 3

export const LastNightCard = ({ day, onOpenRecord }: { day: SleepDayDetail; onOpenRecord: () => void }) => {
  const { metrics, ai } = day
  return (
    <section className="space-y-3 rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-1.5 font-semibold">
          <Moon className="size-4" />
          昨夜の睡眠
        </h2>
        <button
          type="button"
          onClick={onOpenRecord}
          className="flex items-center text-xs text-muted-foreground hover:text-foreground"
        >
          詳しく見る
          <ChevronRight className="size-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="text-2xl font-bold tabular-nums">{formatMinutes(metrics.total_min)}</span>
        {metrics.score !== null && (
          <span className="text-sm text-indigo-600 dark:text-indigo-300">
            スコア {metrics.score}
            {metrics.score_qualifier &&
              `（${QUALIFIER_LABEL.get(metrics.score_qualifier) ?? metrics.score_qualifier}）`}
          </span>
        )}
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatJstTime(metrics.first_start)} → {formatJstTime(metrics.last_end)}
          {metrics.nap_count > 0 && `（昼寝 ${metrics.nap_count}回・${formatMinutes(metrics.nap_min)}）`}
        </span>
      </div>
      {ai && (
        <div className="space-y-2">
          <p className="text-sm font-medium">{ai.headline}</p>
          {ai.actions_today.length > 0 && (
            <div className="space-y-1 rounded-lg bg-emerald-50 p-2.5 dark:bg-emerald-950/40">
              <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-200">今日の過ごし方のポイント</p>
              <ul className="space-y-1">
                {ai.actions_today.map((action) => (
                  <li key={action} className="flex gap-2 text-sm">
                    <ArrowRight className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-300" />
                    <span>{action}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ul className="space-y-1.5">
            {ai.findings.slice(0, MAX_FINDINGS).map((f) => (
              <li key={f.title} className="flex gap-2 text-sm">
                <ToneIcon tone={f.tone} />
                <span>{f.title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
